import { keepPreviousData } from '@tanstack/react-query';
import {
    AGGREGATE_NAMES,
    LOAN_REQUEST_OFFER_SOURCES,
    LOAN_REQUEST_RESOLVERS,
    OFFER_HISTORY_STATUSES,
} from 'cpool-constants';
import type { SchemaConditions } from 'cpool-schemas';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { schemaLoanRequestResolver_GetBankOffersByLeadId } from 'cpool-tables/backend-resolvers';

import type { OfferEntry } from '~/features/leadDetails/components/BankCreditRequest/OfferHistory/schemas/offers.schema';
import { useAggregateQuery } from '~/queryUtils/useQuery';
import { useQueryPositionStore } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

export const useOfferHistory = (lead: SchemaLoanRequestResolver_Get) => {
    const queryKey = queryKeys.getBankOffersByLeadId(lead.id);
    const queryPosition = useQueryPositionStore((state) => state.positions[JSON.stringify(queryKey)]);
    const { data, isLoading } = useAggregateQuery({
        acl: { loanRequestId: lead.id },
        aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
        resolverName: LOAN_REQUEST_RESOLVERS.getBankOffersByLeadId,
        schema: schemaLoanRequestResolver_GetBankOffersByLeadId,
        searchParams: {
            id: lead.id,
            position: queryPosition,
        },
        tanstack: {
            placeholderData: keepPreviousData,
        },
    });

    const groupedOfferRequests: {
        [requestId: string]: {
            conditions: SchemaConditions;
            offers: Array<OfferEntry>;
            countCreatedOffers: number;
        };
    } = {};
    if (data?.offerRequests != null && data?.offers != null) {
        [...data.offerRequests]
            .sort((a, b) => b.requestedAt - a.requestedAt)
            .forEach((offerRequest) => {
                if (!groupedOfferRequests[offerRequest.id])
                    groupedOfferRequests[offerRequest.id] = {
                        conditions: offerRequest.conditions,
                        countCreatedOffers: 0,
                        offers: [],
                    };
                else groupedOfferRequests[offerRequest.id].conditions = offerRequest.conditions;
            });
        data.offers.forEach((offersEntry) => {
            groupedOfferRequests[offersEntry.requestId].countCreatedOffers = offersEntry.offers.filter(
                (offer) =>
                    offer.status !== OFFER_HISTORY_STATUSES.NOT_REQUESTABLE &&
                    offer.status !== OFFER_HISTORY_STATUSES.NOT_REQUESTED &&
                    offer.status !== OFFER_HISTORY_STATUSES.REJECTED &&
                    offer.status !== null,
            ).length;
            groupedOfferRequests[offersEntry.requestId].offers = offersEntry.offers.map<OfferEntry>((offer) => ({
                acceptedOffer: offer.acceptedOffer,
                bank: offer.ratenkredit?.produktanbieter?.name ?? '-',
                bankSvg: offer.ratenkredit?.produktanbieter?.logo?.svg ?? '-',
                createdAt: offersEntry.createdAt.toISOString(),
                effectiveInterestRate: offer.gesamtkonditionen?.effektivzins ?? -1,
                id: offer.id,
                loanRuntime: offer.gesamtkonditionen?.laufzeitInMonaten ?? -1,
                monthlyRate: offer.gesamtkonditionen?.rateMonatlich ?? -1,
                originalOfferData: offer,
                processId: lead.europace?.id ?? null,
                produktbezeichnung: offer.ratenkredit?.produktbezeichnung ?? '-',
                requestAmount: offer.gesamtkonditionen?.nettokreditbetrag ?? -1,
                residualDebtInsurance: {
                    applicant1: offer.ratenschutz?.versicherteRisikenAntragsteller1 ?? [],
                    applicant2: offer.ratenschutz?.versicherteRisikenAntragsteller2 ?? [],
                },
                source: LOAN_REQUEST_OFFER_SOURCES.EUROPACE,
                status: offer.status,
            }));
        });
    }

    return { groupedOfferRequests, isLoading };
};
